import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { ShoppingCart, Menu, X, User, LogOut, Shield, ChevronRight, Cpu, Phone, Globe, Mail, MapPin, Facebook, Instagram, Linkedin, Brain, Landmark } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CartDrawer from "@/components/CartDrawer";
import AiAssistant from "@/components/AiAssistant";
import NotificationBell from "@/components/NotificationBell";
import { toast } from "sonner";

const LOGO_STYLE = { backgroundImage: "url('/logo.png')", backgroundSize: "205% auto", backgroundPosition: "center 5%", backgroundRepeat: "no-repeat" };
const NAV = [
  ["Platform", "/platform"], ["Families", "/families"], ["Caregivers", "/caregivers"], ["Businesses", "/businesses"],
  ["Government", "/government"], ["Compliance", "/compliance"], ["Training", "/training"],
  ["Opportunities", "/opportunities"], ["Marketplace", "/marketplace"], ["Academy", "/store"],
  ["Pricing", "/pricing"], ["Technology", "/technology"], ["About", "/about"],
];

function Header() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-blue-950 text-blue-100 text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-10 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5"><Cpu className="w-4 h-4 text-teal-400" /> The Technology Behind Better Care.</span>
            <span className="hidden md:flex items-center gap-1.5"><Globe className="w-4 h-4 text-teal-400" /> Technology solutions available nationwide</span>
          </div>
          <a href="tel:+16145550123" className="flex items-center gap-1.5 font-semibold hover:text-teal-400 transition-colors"><Phone className="w-4 h-4 text-teal-400" /> (614) 555-0123</a>
        </div>
      </div>

      <div className="bg-white/95 backdrop-blur-xl border-b border-blue-900/10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 shrink-0">
              <span className="block h-12 w-12 rounded-xl shrink-0" style={LOGO_STYLE} />
              <div className="leading-tight">
                <div className="font-display font-bold text-xl text-blue-950 leading-none">T5 <span className="text-teal-600">Care</span></div>
                <div className="text-[9px] uppercase tracking-[0.18em] text-blue-500 font-semibold mt-1">Care · Connected by Intelligence</div>
              </div>
            </Link>

            <nav className="hidden xl:flex items-center gap-0.5">
              {NAV.map(([t, p]) => (
                <NavLink key={p} to={p} data-testid={`nav-${t.toLowerCase()}-link`}
                  className={({ isActive }) => `px-2.5 py-2 rounded-lg text-[13px] font-medium transition-colors ${isActive ? "text-teal-700" : "text-slate-700 hover:text-teal-700"}`}>{t}</NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <button data-testid="cart-button" onClick={() => setOpen(true)} className="relative p-2 rounded-lg hover:bg-blue-50">
                <ShoppingCart className="w-5 h-5 text-blue-900" />
                {count > 0 && <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-teal-500 text-white text-[10px] font-bold w-5 h-5 grid place-items-center rounded-full">{count}</span>}
              </button>
              {user && <NotificationBell />}
              {user ? (
                <div className="hidden sm:flex items-center gap-1">
                  {["admin", "executive", "manager", "staff"].includes(user.role) && <Button variant="ghost" size="sm" data-testid="ops-link" onClick={() => navigate("/ops")} className="text-teal-700 gap-1.5"><Brain className="w-4 h-4" /> Ops Center</Button>}
                  {["admin", "executive", "manager", "staff"].includes(user.role) && <Button variant="ghost" size="sm" data-testid="enterprise-link" onClick={() => navigate("/ops/enterprise")} className="text-teal-700 gap-1.5"><Landmark className="w-4 h-4" /> Enterprise</Button>}
                  {["admin", "executive", "manager"].includes(user.role) && <Button variant="ghost" size="sm" data-testid="intel-link" onClick={() => navigate("/intelligence")} className="text-teal-700 gap-1.5"><Brain className="w-4 h-4" /> Intelligence</Button>}
                  {user.role === "admin" && <Button variant="ghost" size="sm" data-testid="admin-link" onClick={() => navigate("/admin")} className="text-amber-700 gap-1.5"><Shield className="w-4 h-4" /> Admin</Button>}
                  <Button variant="ghost" size="sm" data-testid="dashboard-link" onClick={() => navigate("/dashboard")} className="text-blue-900 gap-1.5"><User className="w-4 h-4" /> Portal</Button>
                  <Button variant="ghost" size="icon" data-testid="logout-button" onClick={() => { logout(); navigate("/"); }}><LogOut className="w-4 h-4" /></Button>
                </div>
              ) : (
                <Button variant="ghost" size="sm" data-testid="login-link" onClick={() => navigate("/login")} className="hidden sm:inline-flex gap-1.5 text-blue-900"><User className="w-4 h-4" /> Sign In</Button>
              )}
              <Button data-testid="request-demo-cta" variant="outline" onClick={() => navigate("/request-demo")} className="hidden lg:inline-flex border-blue-900/25 text-blue-900">Request a Demo</Button>
              <Button data-testid="find-care-cta" onClick={() => navigate("/concierge")} className="hidden sm:inline-flex bg-teal-600 hover:bg-teal-700 text-white shadow-md">Find Care</Button>
              <button className="xl:hidden p-2" onClick={() => setMobile(!mobile)} data-testid="mobile-menu-toggle">{mobile ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
            </div>
          </div>
        </div>

        {mobile && (
          <div className="xl:hidden border-t border-blue-900/10 bg-white px-4 py-3 space-y-1">
            {NAV.map(([t, p]) => <NavLink key={p} to={p} onClick={() => setMobile(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-blue-50">{t}</NavLink>)}
            <div className="flex gap-2 pt-2">
              <Button onClick={() => { setMobile(false); navigate("/concierge"); }} className="flex-1 bg-teal-600 text-white">Find Care</Button>
              <Button variant="outline" onClick={() => { setMobile(false); navigate("/request-demo"); }} className="flex-1">Request a Demo</Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

function Footer() {
  const subscribe = (e) => { e.preventDefault(); e.target.reset(); toast.success("Subscribed! Watch your inbox for updates."); };
  const COLS = [
    ["Solutions", [["For Families", "/families"], ["For Caregivers", "/caregivers"], ["For Businesses", "/businesses"], ["For Government", "/government"]]],
    ["Platform", [["T5 Care", "/families"], ["T5 Connect", "/marketplace"], ["T5 Academy", "/store"], ["T5 Intelligence", "/technology"]]],
    ["Company", [["About", "/about"], ["Partner With T5", "/partners"], ["Contact", "/contact"], ["Request a Demo", "/request-demo"]]],
  ];
  return (
    <footer className="bg-blue-950 text-blue-100/80 mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-5 gap-10">
        <div className="col-span-2">
          <div className="flex items-center gap-2.5 mb-4">
            <span className="block h-12 w-12 rounded-xl bg-white shrink-0" style={LOGO_STYLE} />
            <div><div className="font-display font-bold text-lg text-white leading-none">T5 Care</div><div className="text-[10px] uppercase tracking-[0.18em] text-teal-400 font-semibold mt-1">Care · Connected by Intelligence</div></div>
          </div>
          <p className="text-sm leading-relaxed max-w-sm">One intelligent care ecosystem connecting families, caregivers, healthcare organizations, employers and public-sector programs.</p>
          <p className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold bg-teal-500/15 text-teal-300 px-3 py-1.5 rounded-full">Proudly Black-Owned &amp; Minority-Owned</p>
          <div className="flex gap-2 mt-4">{[Facebook, Instagram, Linkedin].map((Icon, i) => <span key={i} className="grid place-items-center w-9 h-9 rounded-lg bg-white/10 hover:bg-teal-500 transition-colors cursor-pointer"><Icon className="w-4 h-4 text-white" /></span>)}</div>
        </div>
        {COLS.map(([title, links]) => (
          <div key={title}>
            <h4 className="text-white font-semibold mb-4 text-sm">{title}</h4>
            <ul className="space-y-2.5 text-sm">{links.map(([t, p]) => <li key={t}><Link to={p} className="hover:text-teal-400 transition-colors">{t}</Link></li>)}</ul>
          </div>
        ))}
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 grid md:grid-cols-2 gap-6 items-center border-t border-white/10 pt-8">
        <div className="text-sm space-y-2">
          <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-teal-400" /> (614) 555-0123 &nbsp;·&nbsp; <Mail className="w-4 h-4 text-teal-400" /> Info@t5care.com</p>
          <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-teal-400" /> Columbus, OH · Care services in Ohio · Technology available nationwide</p>
        </div>
        <form onSubmit={subscribe} className="flex gap-2 md:justify-end">
          <Input type="email" required placeholder="Your email" data-testid="newsletter-input" className="bg-white/10 border-white/20 text-white placeholder:text-blue-200/60 max-w-xs" />
          <Button type="submit" data-testid="newsletter-submit" className="bg-teal-500 hover:bg-teal-600 text-white shrink-0">Subscribe</Button>
        </form>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-blue-100/50">© {new Date().getFullYear()} T5 Care · Care services provided in Ohio (Ohio Dept of Aging PASSPORT approved) · Technology solutions available nationwide</div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="App min-h-screen flex flex-col bg-[#FAF9F6]">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <CartDrawer />
      <AiAssistant />
    </div>
  );
}
